import { useState, useEffect, useRef } from "react";

const PRIMARY_COLOR = "var(--vz-primary, #f06b2f)";
const PRIMARY_LIGHT = "rgba(240, 107, 47, 0.08)";
const BORDER_LIGHT_BLUE = "#cfd8ea";
const ROW_BORDER_LIGHT_BLUE = "#dde4f0";

export default function MultiSelect({
  name,
  options = [],
  value = [],
  handleChange,
  handleBlur,
  invalid,
  disabled,
  placeholder,
  className,
  style,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        if (handleBlur) handleBlur();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, handleBlur]);

  const toggleOption = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((v) => v !== opt)
      : [...selected, opt];
    if (handleChange) handleChange(next);
  };

  const removeOption = (opt) => {
    if (handleChange) handleChange(selected.filter((v) => v !== opt));
  };

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ position: "relative", ...style }}
    >
      {/* selected chips + arrow */}
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "6px",
          padding: "8px 36px 8px 10px",
          border: `1px solid ${invalid ? "#f06548" : BORDER_LIGHT_BLUE}`,
          borderRadius: "8px",
          background: disabled ? "#f3f6f9" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
      >
        {selected.length === 0 && placeholder ? (
          <span style={{ fontSize: "13px", color: "#878a99" }}>
            {placeholder}
          </span>
        ) : null}

        {selected.map((val) => (
          <span
            key={val}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              padding: "2px 10px",
              borderRadius: "12px",
              background: "#eef0f4",
              border: "1px solid #dfe3ea",
              color: "#343a40",
            }}
          >
            {val}
            <span
              style={{ cursor: "pointer", fontSize: "11px", color: "#6c757d" }}
              onClick={(e) => {
                e.stopPropagation();
                removeOption(val);
              }}
            >
              ✕
            </span>
          </span>
        ))}
        <i
          className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "18px",
          }}
        />
      </div>

      {/* options (checkbox) */}
      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: "4px",
            maxHeight: "220px",
            overflowY: "auto",
            border: `1px solid ${BORDER_LIGHT_BLUE}`,
            borderRadius: "4px",
            background: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {options.map((opt, i) => {
            const checked = selected.includes(opt);
            return (
              <div
                key={opt}
                onClick={() => toggleOption(opt)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom:
                    i === options.length - 1
                      ? "none"
                      : `1px solid ${ROW_BORDER_LIGHT_BLUE}`,
                  fontSize: "13px",
                  color: "#343a40",
                  background: checked ? PRIMARY_LIGHT : "#fff",
                }}
              >
                {/* checkbox */}
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    flexShrink: 0,
                    borderRadius: "3px",
                    border: `1.5px solid ${checked ? PRIMARY_COLOR : "#9aa5b5"}`,
                    background: checked ? PRIMARY_LIGHT : "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 10 10">
                      <path
                        d="M1.5 5.2 L4 7.6 L8.5 2.4"
                        fill="none"
                        stroke="#d9531e"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>

                <span style={{ flexGrow: 1 }}>{opt}</span>

                {/* clear this option */}
                <span
                  title="Clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(opt);
                  }}
                  style={{
                    fontSize: "13px",
                    lineHeight: 1,
                    color: "#343a40",
                    padding: "0 2px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}